import { uploadFile } from '@app/api/file.api';
import { getAWSSignedURL } from '@app/api/general.api';
import { getUserByAuth, getUserListWithPagination, getUserListWithQuery, updateUser } from '@app/api/users.api';
import { Entity } from '@app/constants/enums/entites';
import { GenderEnum } from '@app/constants/enums/gender';
import { FileUploadRequest, SearchQueryRequest } from '@app/interfaces/interfaces';
import { Dispatch, PrepareAction, createAction, createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import type { UploadProps } from 'antd';
import { isObject } from 'lodash';
import { doLogout } from './authSlice';
import { IUser } from '@app/interfaces/user';

export interface UserState {
  user: IUser | null;
  client: IUser | null;
  isAdmin: boolean;
}

export interface ClientRequest {
  name?: string;
  email?: string;
  password?: string;
  image?: UploadProps | string;
  firstname?: string;
  lastname?: string;
  gender?: GenderEnum | string;
  phone?: string;
  phonePrefix?: string | number;
  zipcode?: string | number;
  birthdate?: string;
  address1?: string;
  address2?: string;
  city?: string;
  country?: string;
}

export interface UpdateUserRequest {
  userDetails: ClientRequest;
  userId: string;
}

export interface QueryRequest {
  page?: number;
  limit?: number;
  search?: string;
  sortBy?: string;
}

const initialState: UserState = { user: null, client: null, isAdmin: false };

export const setUser = createAction<PrepareAction<IUser>>('user/setUser', (newUser) => {
  return {
    payload: newUser,
  };
});

export const setClient = createAction<PrepareAction<IUser>>('user/setClient', (newClient) => {
  return {
    payload: newClient,
  };
});

export const setIsAdmin = createAction<PrepareAction<boolean>>('user/setIsAdmin', (isAdmin) => {
  return {
    payload: isAdmin,
  };
});

export const doUpdateUserProfile = createAsyncThunk(
  'user/updateUserProfile',
  async (clientPayload: UpdateUserRequest, { dispatch }) =>
    updateUser(clientPayload).then((res) => {
      if (res) {
        setUserDetails(res, dispatch);
      }
    }),
);

export const doUpdateUser = createAsyncThunk('user/updateUser', async (clientPayload: UpdateUserRequest) =>
  updateUser(clientPayload),
);

export const fetchUser = createAsyncThunk('auth/getUserDetails', async (unknown, { dispatch }) =>
  getUserByAuth()
    .then(async (res) => {
      setUserDetails(res, dispatch);
    })
    .catch(() => {
      dispatch(doLogout());
    }),
);

export const doUploadProfilePicture = createAsyncThunk(
  'user/updateUserProfile',
  async (filePayload: FileUploadRequest) => uploadFile(filePayload),
);

export const fetchUserListWithPagination = createAsyncThunk(
  'user/getUserlistWithPagination',
  async (queryPayload: SearchQueryRequest) => getUserListWithPagination(queryPayload),
);

export const fetchUserListWithQuery = createAsyncThunk('user/getUserlist', async (queryPayload: SearchQueryRequest) =>
  getUserListWithQuery(queryPayload),
);

export const getImageSignedURL = async (filename: string, entity: string): Promise<string> => {
  const res = await getAWSSignedURL({ filename, entity });
  return res.toString();
};

export const setUserDetails = async (user: IUser, dispatch: Dispatch, isClient = false): Promise<IUser> => {
  const profileImageURL: string | null =
    user?.image?.name && (await getImageSignedURL(user?.image?.name, `${user?.image?.entity}`));
  if (profileImageURL) {
    user.image.signedURL = profileImageURL.toString();
  }
  const role = isObject(user?.role) ? user?.role?.id : user?.role;
  dispatch(setIsAdmin(role === process.env.REACT_APP_DEFAULT_ADMIN_ROLE));
  if (isClient) {
    dispatch(setClient(user));
  } else {
    dispatch(setUser(user));
  }
  return user;
};

export const userSlice = createSlice({
  name: Entity.USER,
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(setUser, (state, action) => {
      state.user = action.payload;
    });
    builder.addCase(setClient, (state, action) => {
      state.client = action.payload;
    });
    builder.addCase(setIsAdmin, (state, action) => {
      state.isAdmin = action.payload;
    });
  },
});

export default userSlice.reducer;
